import type { FC, JSX } from 'react';

import { acceptedKeyValueCheck, cn, humanize, splitCamelCaseToString } from '@pesto/utils';
import { useCallback } from 'react';

import { displayValueSchema, formatApplicationValues } from '../utils/dataDisplayHelpers';

interface DataListProps {
  root?: object;
  span?: string;
  spaced?: boolean;
  separator?: boolean;
  keepKeys?: boolean;
  itemClassName?: string;
  noDataText?: string;
  containerClassName?: string;
  className?: string;
}

export const DataList: FC<DataListProps> = ({
  root,
  spaced = false,
  separator = false,
  itemClassName,
  containerClassName,
  keepKeys,
  className,
  noDataText = '-',
}) => {
  const wrapperStyle = cn('flex flex-col gap-2', className);

  const renderProperties = useCallback(() => {
    const properties: JSX.Element[] = [];

    const itemStyle = cn(itemClassName, {
      'border-b': separator,
    });

    const containerStyle = cn('text-foreground/60 font-medium', containerClassName, {
      'flex gap-4 justify-between': spaced,
    });

    if (root) {
      Object.entries(root).forEach(([key, value]) => {
        if (keepKeys) {
          return properties.push(
            <div key={key} className={itemStyle}>
              <div className={containerStyle}>
                {humanize(splitCamelCaseToString(key) ?? '')}
                <div className={'text-foreground'}>{formatApplicationValues(key, value) ?? noDataText}</div>
              </div>
            </div>,
          );
        }

        if (acceptedKeyValueCheck(key, value)) return null;

        const displayValue = displayValueSchema.parse(value);

        if (displayValue.kind === 'nested-list' || displayValue.kind === 'list') {
          if (displayValue.value.length === 0) return null;
        }

        if (displayValue.kind === 'nested-list')
          return properties.push(
            ...displayValue.value.map((item, index) => (
              <DataList
                key={`${key}-${index}`}
                root={item}
                className={wrapperStyle}
                containerClassName={containerStyle}
                itemClassName={itemStyle}
              />
            )),
          );

        if (displayValue.kind === 'list')
          return properties.push(
            <div key={key} className={itemStyle}>
              <div className={containerStyle}>
                {humanize(splitCamelCaseToString(key) ?? '')}
                <div className={'text-foreground'}>{displayValue.value.map(String).join(', ')}</div>
              </div>
            </div>,
          );

        if (displayValue.kind === 'record' || displayValue.kind === 'component')
          return properties.push(
            <DataList
              key={key}
              root={value}
              className={wrapperStyle}
              containerClassName={containerStyle}
              itemClassName={itemStyle}
            />,
          );

        return properties.push(
          <div key={key} className={itemStyle}>
            <div className={containerStyle}>
              {humanize(splitCamelCaseToString(key) ?? '')}
              <div className={'text-foreground'}>{formatApplicationValues(key, value) ?? noDataText}</div>
            </div>
          </div>,
        );
      });
    }

    return properties;
  }, [root, spaced, itemClassName, containerClassName, separator, keepKeys, noDataText, wrapperStyle]);

  return <div className={wrapperStyle}>{renderProperties()}</div>;
};
